import axios from 'axios'
import router from 'next/router'
import { createContext, useContext, useEffect, useState } from 'react'
import { useCookies } from 'react-cookie'

import Body from '~/components/typography/Body'
import Bravo from '~/components/typography/Bravo'
import { STARTLE_MESSAGE_TO_STATE } from '~/utils/startle'

const SimplifiedContext = createContext(false)

// Whether this page load is a Willow embed specifically, as opposed to any
// simplified embed. Startle is also simplified but talks to us over the URL,
// not postMessage, so anything that posts back to the parent frame — the cast
// button in QuizNav — has to check this rather than `isSimplified`.
const WillowEmbedContext = createContext(false)

// The `name` a Willow embed gives its iframe, marking the frame as an embed
// from the moment it's created. Kept in sync with public/embed.html.
const WILLOW_FRAME_NAME = 'kwizzbit-willow'

// The same trick for Startle. Startle flags itself with ?integration=startle,
// but that param only survives the first load: resolveStartle() finishes with
// router.push('/dashboard'), which drops the query string. Any full page load
// after that — a refresh, one of the window.location.reload() calls in the
// quiz flow, or the parent re-creating the frame — would otherwise come back
// with no embed marker at all and drop the user into the full app with the
// menu showing. The name is written once, in the pre-paint script in
// _document.js, and survives every navigation and reload within the frame.
const STARTLE_FRAME_NAME = 'kwizzbit-startle'

// How long we'll wait for a Willow parent to answer our ready ping before
// giving up. Without this a parent that never posts config leaves the user
// looking at a blank frame forever.
const WILLOW_CONFIG_TIMEOUT = 10000

function WillowAuthError() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-30">
      <div className="max-w-md text-center text-body">
        <div className="text-5xl mb-20">⚠️</div>
        <Bravo className="mb-16">Unable to sign you in</Bravo>
        <Body className="mb-10">
          Your ID could not be verified. Please check the following:
        </Body>
        <ul className="text-left list-disc pl-24 mb-20 space-y-8">
          <li>
            <Body>You have an active KwizzBit paid plan</Body>
          </li>
          <li>
            <Body>
              Your ID has been added to your KwizzBit account settings
            </Body>
          </li>
          <li>
            <Body>The ID being passed matches the one on your account</Body>
          </li>
        </ul>
        <Body className="text-neutral-400 text-sm">
          If you continue to have issues, please contact KwizzBit support.
        </Body>
      </div>
    </div>
  )
}

function StartleAuthError() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-30">
      <div className="max-w-md text-center text-body">
        <div className="text-5xl mb-20">⚠️</div>
        <Bravo className="mb-16">Unable to sign you in</Bravo>
        <Body className="mb-10">
          We couldn&apos;t verify this Startle device with KwizzBit. Please try
          again in a moment.
        </Body>
        <Body className="text-neutral-400 text-sm">
          If you continue to have issues, please contact KwizzBit support.
        </Body>
      </div>
    </div>
  )
}

// Which partner this page load is embedded by. Read straight off the URL so
// the very first client render already knows, otherwise the app paints a full
// dashboard for a beat — and NavContainer bounces the un-authenticated route
// to /login — before we've had a chance to log the user in.
function embedIntegration() {
  if (typeof window === 'undefined') return null
  return new URLSearchParams(window.location.search).get('integration')
}

function isStartleEmbed() {
  if (typeof window === 'undefined') return false
  return window.name === STARTLE_FRAME_NAME || embedIntegration() === 'startle'
}

// Willow resolves its login over postMessage rather than the URL, but the
// embed still has to flag itself up front so we know to hold the render.
// It does that with the iframe's `name` attribute rather than a query param:
// the browser sets it on the frame before the document starts parsing, so the
// hide script in _document and our very first render both see it, and unlike
// the query string it survives the user navigating around inside the frame.
// The query param is still honoured for embeds set up the older way.
function isWillowEmbed() {
  if (typeof window === 'undefined') return false
  return window.name === WILLOW_FRAME_NAME || embedIntegration() === 'willow'
}

function isEmbed() {
  return isStartleEmbed() || isWillowEmbed()
}

// Whether THIS load still has an embed login to resolve, which is not the same
// question as "is this an embed".
//
// Willow waits every time: we post kwizzbit:ready on each load and the parent
// answers with its config. Startle only has work to do on the load carrying
// the code in the URL — a later load in the same frame is already
// authenticated by the cookie. Gating on isEmbed() instead would leave those
// later Startle loads waiting for a resolveStartle() that never runs, and the
// provider renders null until auth is ready, so the frame would stay blank.
function hasPendingEmbedAuth() {
  if (typeof window === 'undefined') return false
  return isWillowEmbed() || embedIntegration() === 'startle'
}

// Store a token the same way a normal login does: the cookie is what the rest
// of the app reads first, with sessionStorage as the fallback for contexts
// where the cookie isn't available.
//
// In production the embed runs in an iframe on the partner's domain, so the
// cookie is cross-site — browsers only store it when it's explicitly
// SameSite=None and Secure. Same-site/top level keeps the default Lax.
function storeAuthToken(setCookie, token, label) {
  const isFramed = window.parent !== window

  try {
    setCookie('auth_token', token, {
      path: '/',
      ...(isFramed ? { sameSite: 'none', secure: true } : {})
    })
  } catch (err) {
    console.log(`[kwizzbit:${label}] could not set auth cookie`, err)
  }

  try {
    sessionStorage.setItem('auth_token', token)
  } catch (err) {
    // Storage can be blocked outright in a third-party frame.
    console.log(`[kwizzbit:${label}] could not write sessionStorage`, err)
  }
}

export function SimplifiedProvider({ children }) {
  const [isSimplified, setIsSimplified] = useState(isEmbed)
  // Fixed for the lifetime of the page: it comes from the iframe's name or the
  // integration param, neither of which changes after load.
  const [isWillow] = useState(isWillowEmbed)
  const [isAuthReady, setIsAuthReady] = useState(() => !hasPendingEmbedAuth())
  const [willowAuthFailed, setWillowAuthFailed] = useState(false)
  const [startleAuthFailed, setStartleAuthFailed] = useState(false)
  const [, setCookie] = useCookies(['auth_token'])

  useEffect(() => {
    // _document hides the document for embeds so the server-rendered app
    // never paints before we know where the user belongs. We're hydrated
    // now, so React is in charge of what shows.
    document.documentElement.style.visibility = ''
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') return

    const params = Object.fromEntries(
      new URLSearchParams(window.location.search)
    )

    console.log('[kwizzbit:embed]', {
      inIframe: window.parent !== window,
      referrer: document.referrer,
      integration: params.integration
    })

    if (params.integration !== 'startle') return

    // Resolve the Startle device against a KwizzBit account. Either we get a
    // token back and log them straight in, or we get a message telling us
    // which set up step they still need to complete.
    const resolveStartle = async () => {
      let data
      try {
        const res = await axios.post(`${process.env.base_api}/auth/token`, {
          startle_token: params.code,
          startle_device_id: parseInt(params.device_id, 10),
          device_name: 'startle'
        })
        data = res.data
      } catch (err) {
        // The set up states may come back as an error response, so only bail
        // out here if there's no message we recognise.
        data = err.response?.data
        if (!STARTLE_MESSAGE_TO_STATE[data?.message]) {
          console.log('[kwizzbit:startle] resolve failed', err)
          setStartleAuthFailed(true)
          return
        }
      }

      const upgradeState = STARTLE_MESSAGE_TO_STATE[data?.message]

      // Navigate first and only open the gate once we've landed. Rendering
      // the current route in between would mount the app un-authenticated,
      // and NavContainer bounces that straight to /login.
      if (upgradeState) {
        try {
          await router.push(`/startle-upgrade?state=${upgradeState}`)
        } finally {
          setIsAuthReady(true)
        }
        return
      }

      const token = data?.token?.auth_token

      if (!token) {
        console.log('[kwizzbit:startle] no token in response', data)
        setStartleAuthFailed(true)
        return
      }

      storeAuthToken(setCookie, token, 'startle')

      try {
        await router.push('/dashboard')
      } finally {
        setIsAuthReady(true)
      }
    }

    resolveStartle()
  }, [setCookie])

  useEffect(() => {
    const willow = isWillowEmbed()
    let timer = null

    const handleMessage = async (e) => {
      if (e.data?.type !== 'kwizzbit:config') return
      if (timer) clearTimeout(timer)

      const simplified = e.data.simplified === true
      setIsSimplified(simplified)

      if (simplified && e.data.willowId) {
        let token = null

        try {
          token = sessionStorage.getItem('auth_token')
        } catch (err) {
          // Storage can be blocked outright in a third-party frame.
          console.log('[kwizzbit:willow] could not read sessionStorage', err)
        }

        if (!token) {
          try {
            // The API matches on `willow_id` — see
            // App\Http\Requests\Api\Auth\CreateTokenRequest. Anything else
            // falls through to the email/password path and 422s.
            const res = await axios.post(`${process.env.base_api}/auth/token`, {
              willow_id: e.data.willowId,
              device_name: 'willow'
            })
            token = res.data?.token?.auth_token
          } catch (err) {
            console.log('[kwizzbit:willow] resolve failed', err)
            setWillowAuthFailed(true)
            return
          }
        }

        if (!token) {
          console.log('[kwizzbit:willow] no token in response')
          setWillowAuthFailed(true)
          return
        }

        storeAuthToken(setCookie, token, 'willow')
      }

      setIsAuthReady(true)
    }

    window.addEventListener('message', handleMessage)

    if (window.parent !== window) {
      window.parent.postMessage({ type: 'kwizzbit:ready' }, '*')
    }

    // We're holding the whole app back waiting on this handshake, so don't
    // hold it forever if the parent never replies.
    if (willow) {
      timer = setTimeout(() => {
        console.log('[kwizzbit:willow] no config message from parent')
        setWillowAuthFailed(true)
      }, WILLOW_CONFIG_TIMEOUT)
    }

    return () => {
      window.removeEventListener('message', handleMessage)
      if (timer) clearTimeout(timer)
    }
  }, [setCookie])

  if (willowAuthFailed) return <WillowAuthError />
  if (startleAuthFailed) return <StartleAuthError />
  if (!isAuthReady) return null

  return (
    <SimplifiedContext.Provider value={isSimplified}>
      <WillowEmbedContext.Provider value={isWillow}>
        {children}
      </WillowEmbedContext.Provider>
    </SimplifiedContext.Provider>
  )
}

export function useSimplified() {
  return useContext(SimplifiedContext)
}

/** True only inside a Willow embed — false for Startle and for normal use. */
export function useIsWillowEmbed() {
  return useContext(WillowEmbedContext)
}
